import { useCallback, useMemo } from "react";
import intersectionWith from "lodash/intersectionWith";
import isEmpty from "lodash/isEmpty";
import isEqual from "lodash/isEqual";

import useFilters from "hooks/useFilters";

import { Facet } from "components/FilteredGrid";

import {
  MenuFilterGroupValues,
  MenuFilterMap,
  MenuFiltersGroup,
} from "../types/MenuFilters";

// * These query params aren't related to our filtered grid / pills display, and are handled elsewhere.
const IGNORED_QUERIES = [
  "dispensarySort",
  "fulfillmentPreference",
  "attemptedRetailerName",
];

/**
 * This hook creates facets that are used by the FilterChip.
 * It ensures that facet data used by this component has both
 * the correct label and value.
 */

const useAPISupportedFilterFacets = (
  menuFilterMap?: MenuFilterMap,
  allowNonAPISupportedFacets = false,
) => {
  const { filterValues } = useFilters({
    ignoredQueries: IGNORED_QUERIES,
  });
  const createFacet = useCallback(
    (key: string, value: string, label?: string) => {
      return {
        key: `${key}[]`,
        label: label ? label : value,
        value,
      };
    },
    [],
  );

  const createToggleFacet = useCallback(
    (key: string, value: string) => {
      if (isEmpty(menuFilterMap) || menuFilterMap === undefined) {
        return [];
      }

      const filter = menuFilterMap[key];
      if (!filter) {
        return;
      }

      return {
        key,
        label: filter.label,
        value,
      };
    },
    [menuFilterMap],
  );

  const isolateFilteredFacetsArray = useCallback(
    (key: string, appliedFilters: string[]) => {
      if (!menuFilterMap) {
        return [];
      }
      const apiSupportedFilterCategory = menuFilterMap[key] as MenuFiltersGroup;
      const apiSupportedFilters: MenuFilterGroupValues[] =
        apiSupportedFilterCategory?.values || [];
      const apiSupportedValues = intersectionWith(
        apiSupportedFilters,
        appliedFilters,
        ({ value }, v) => isEqual(value, v),
      );
      let facets = apiSupportedValues.map(({ value, chip }) =>
        createFacet(key, value, chip),
      );

      // * Our backend filter aggregations are limited to 1000 entries. This option allows us to still show UI elements for filters that may not be returned in the API call.
      if (allowNonAPISupportedFacets) {
        const apiSupportedFilterValues = facets.map((f) => f.value);
        const nonAPISupportedValues =
          appliedFilters.filter(
            (appliedFilter) =>
              !apiSupportedFilterValues.includes(appliedFilter),
          ) || [];

        facets = [
          ...facets,
          ...nonAPISupportedValues.map((nonAPIFilterValue) =>
            createFacet(key, nonAPIFilterValue),
          ),
        ];
      }

      return facets;
    },
    [menuFilterMap, createFacet, allowNonAPISupportedFacets],
  );

  const apiSupportedFacets = useMemo(
    (): Facet[] =>
      Object.keys(filterValues || []).reduce(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
        (prevFacets: any[], key: string) => {
          const value = filterValues[key];
          const valueIsSearchQuery = key === "q";
          const facetIsToggle = value === "true";
          if (!value) {
            return [];
          }
          if (facetIsToggle) {
            const facet = [createToggleFacet(key, value)];
            return prevFacets.concat(facet);
          }
          // * NOTE: When value is a search query, filterValues returns a string
          // * which is why the falsy statement calls createFacet directly.
          const facets = valueIsSearchQuery
            ? [createFacet(key, value as string)]
            : isolateFilteredFacetsArray(key, value as string[]);

          return prevFacets.concat(facets);
        },
        [],
      ),
    [isolateFilteredFacetsArray, createFacet, createToggleFacet, filterValues],
  );

  return apiSupportedFacets;
};

export default useAPISupportedFilterFacets;
